import React from 'react';
import { AlertTriangle, X, ExternalLink } from 'lucide-react';
import { Customer } from '../../../shared/types';
import { buildZendeskAgentSuchUrl } from '../../../shared/services/zendesk';

interface ZendeskWarningModalProps {
  isOpen: boolean;
  customer: Customer;
  // null = Zendesk konnte nicht geprüft werden (Abfrage fehlgeschlagen). Es ist
  // dann unbekannt, ob Tickets offen sind; das Fenster weist darauf hin, statt
  // die Finalisierung stillschweigend durchzulassen.
  ticketCount: number | null;
  onProceed: () => void;
  onCancel: () => void;
}

export const ZendeskWarningModal: React.FC<ZendeskWarningModalProps> = ({
  isOpen,
  customer,
  ticketCount,
  onProceed,
  onCancel
}) => {
  if (!isOpen) return null;

  const pruefungFehlgeschlagen = ticketCount === null;

  // Gleiche Suchanfrage wie die Zählung, damit die Trefferliste in Zendesk zur
  // angezeigten Zahl passt.
  const zendeskUrl = customer.zendesk_tag 
    ? buildZendeskAgentSuchUrl(customer.zendesk_tag)
    : null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1001 // Höher als UploadModal
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '450px',
        width: '90%',
        boxShadow: '0 20px 25px rgba(0, 0, 0, 0.1)',
        border: '2px solid #F59E0B' // Orange Warnung
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          marginBottom: '20px' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AlertTriangle size={24} color="#F59E0B" />
            <h3 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: 'bold',
              color: '#92400E' 
            }}>
              {pruefungFehlgeschlagen ? 'Zendesk konnte nicht geprüft werden' : 'Zendesk-Tickets offen'}
            </h3>
          </div>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px'
            }}
          >
            <X size={20} color="#6B7280" />
          </button>
        </div>

        {/* Customer Info */}
        <div style={{ 
          marginBottom: '20px', 
          padding: '12px', 
          backgroundColor: '#FEF3C7', 
          borderRadius: '8px',
          border: '1px solid #F59E0B'
        }}>
          <p style={{ margin: '0 0 4px 0', fontWeight: '500' }}>
            {customer.company}
          </p>
          <p style={{ margin: 0, fontSize: '14px', color: '#92400E' }}>
            {pruefungFehlgeschlagen
              ? 'Anzahl offener Tickets unbekannt'
              : ticketCount === 1 
                ? '1 offenes Ticket gefunden' 
                : `${ticketCount} offene Tickets gefunden`
            }
          </p>
        </div>

        {/* Warning Message */}
        <div style={{ marginBottom: '20px' }}>
          {pruefungFehlgeschlagen ? (
            <p style={{ 
              fontSize: '14px', 
              color: '#374151', 
              lineHeight: '1.5',
              margin: '0 0 12px 0'
            }}>
              Die Abfrage bei Zendesk ist fehlgeschlagen. Ob für diesen Kunden noch Tickets offen sind,
              lässt sich deshalb gerade nicht sagen. Bitte prüfen Sie die Tickets in Zendesk, bevor Sie
              den Monat abschließen.
            </p>
          ) : (
            <p style={{ 
              fontSize: '14px', 
              color: '#374151', 
              lineHeight: '1.5',
              margin: '0 0 12px 0'
            }}>
              Für diesen Kunden sind noch <strong>{ticketCount} Zendesk-Ticket{ticketCount !== 1 ? 's' : ''}</strong> offen. 
              Es wird empfohlen, diese vor dem Monatsabschluss zu bearbeiten.
            </p>
          )}
          <p style={{ 
            fontSize: '14px', 
            color: '#6B7280', 
            margin: 0
          }}>
            Möchten Sie den Monat trotzdem abschließen?
          </p>
        </div>

        {/* Zendesk Link */}
        {zendeskUrl && (
          <div style={{ marginBottom: '20px' }}>
            <a
              href={zendeskUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                color: '#3B82F6',
                textDecoration: 'none',
                fontSize: '14px',
                padding: '8px 12px',
                backgroundColor: '#EFF6FF',
                borderRadius: '6px',
                border: '1px solid #DBEAFE'
              }}
            >
              <ExternalLink size={16} />
              Tickets in Zendesk öffnen
            </a>
          </div>
        )}

        {/* Actions */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end' 
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              backgroundColor: '#F3F4F6',
              color: '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={onProceed}
            style={{
              padding: '8px 16px',
              backgroundColor: '#F59E0B',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <AlertTriangle size={16} />
            Trotzdem abschließen
          </button>
        </div>
      </div>
    </div>
  );
};
